import React from 'react';
import RowTypography from 'components/Table/RowTypography';
import {Chip } from '@mui/material';

type CapacityStatus = 'Healthy' | 'At risk' | 'Throttled';
type UserStatus = 'Active' | 'At risk' | 'Inactive';
type Status = CapacityStatus | UserStatus;


interface StatusBadgeProps {
	status: Status;
}

export const StatusBadge: React.FC<StatusBadgeProps> = ({ status }) => {
  const getStatusConfig = (status: Status) => {
    switch (status) {
      case 'Healthy':
      case 'Active':
        return { color: '#FFFFFF', bgcolor: 'rgb(54, 168, 255)' }; 
      case 'At risk':
        return { color: '#FFFFFF', bgcolor: '#ECB914' }; // Orange
      case 'Throttled':
      case 'Inactive':
        return { color: '#FFFFFF', bgcolor: '#FE2626' }; // Red
      default:
        return { color: '#FFFFFF', bgcolor: 'rgba(158, 158, 158, 0.2)' };
    }
  };

  const config = getStatusConfig(status);

  return (
    <Chip
      label={status}
      title={status}
      size="small"
      variant="chipLabel"
      tabIndex={-1}
      sx={{
        backgroundColor: `${config.bgcolor} !important`,
        color: config.color,
        height: '24px',
        borderRadius: '4px',
        border: `1px solid ${config.color}`,
        '& .MuiChip-label': {
          padding: '0 8px',
          color: config.color
        }
      }}
    />
  );
};

const StatusBadgeCell = ({ status }: { status: Status }) => {
  return <StatusBadge status={status} />;
};

export const StatusFormatter = ({ row }: { row: any }) => {
  const rawStatus = row.status || row.activityStatus;

  const validStatuses: Status[] = ['Healthy', 'At risk', 'Throttled', 'Active', 'Inactive'];

  if (typeof rawStatus !== 'string' || !validStatuses.includes(rawStatus as Status)) {
    return <RowTypography>--</RowTypography>;
  }

  return <StatusBadgeCell status={rawStatus as Status} />;
};

export const LastRefreshDateFormatter = ({ row }: { row: any }) => {
  const lastRefreshTime = row.lastRefreshTime;

  if (!lastRefreshTime || lastRefreshTime === '--') {
    return <RowTypography>--</RowTypography>;
  }

  // Ensure the date string has 'Z' for UTC
  const dateString = lastRefreshTime.endsWith('Z') ? lastRefreshTime : lastRefreshTime + 'Z';
  const d = new Date(dateString);

  const year = d.getUTCFullYear();
  const month = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  const hours = String(d.getUTCHours()).padStart(2, '0');
  const minutes = String(d.getUTCMinutes()).padStart(2, '0');

  const formattedDate = `${year}-${month}-${day} ${hours}h:${minutes}m`;

  return <RowTypography>{formattedDate}</RowTypography>;
};

// Basic formatters
export const CapacityIdFormatter = ({ row }: { row: any }) => <RowTypography>{row.capacityId || '--'}</RowTypography>;
export const CapacityNameFormatter = ({ row }: { row: any }) => <RowTypography>{row.capacityName || '--'}</RowTypography>;
export const SkuFormatter = ({ row }: { row: any }) => <RowTypography>{row.sku || '--'}</RowTypography>;
export const SectorFormatter = ({ row }: { row: any }) => <RowTypography>{row.sector || '--'}</RowTypography>;
export const DatasetNameFormatter = ({ row }: { row: any }) => <RowTypography>{row.datasetName || '--'}</RowTypography>;
export const LastRefreshDurationFormatter = ({ row }: { row: any }) => <RowTypography>{row.lastRefreshDuration || '--'}</RowTypography>;
export const WorkspaceNameFormatter = ({ row }: { row: any }) => <RowTypography>{row.workspaceName || '--'}</RowTypography>;
export const LastRefreshStatusFormatter = ({ row }: { row: any }) => <RowTypography>{row.lastRefreshStatus || '--'}</RowTypography>;
export const FailureReasonFormatter = ({ row }: { row: any }) => <RowTypography>{row.failureReason || '--'}</RowTypography>;
export const OperationFormatter = ({ row }: { row: any }) => <RowTypography>{row.operation || '--'}</RowTypography>;
export const UserEmailFormatter = ({ row }: { row: any }) => <RowTypography>{row.userEmail || '--'}</RowTypography>;
export const OperationTimeFormatter = ({ row }: { row: any }) => <RowTypography>{row.operationTime || '--'}</RowTypography>;
export const ReportNameFormatter = ({ row }: { row: any }) => <RowTypography>{row.reportName || '--'}</RowTypography>;
export const WorkspaceFormatter = ({ row }: { row: any }) => <RowTypography>{row.workspace || '--'}</RowTypography>;
export const CapacityOwnerFormatter = ({ row }: { row: any }) => <RowTypography>{row.capacityOwner || '--'}</RowTypography>;
export const WorkspaceOwnerFormatter = ({ row }: { row: any }) => <RowTypography>{row.workspaceOwner || '--'}</RowTypography>;
export const ViewCountFormatter = ({ row }: { row: any }) => <RowTypography>{row.viewCount || 0}</RowTypography>;
export const ReportCreationDateFormatter = ({ row }: { row: any }) => <RowTypography>{row.reportCreationDate || '--'}</RowTypography>;
export const OwnerFormatter = ({ row }: { row: any }) => <RowTypography>{row.owner || '--'}</RowTypography>;
export const DatasetCreationDateFormatter = ({ row }: { row: any }) => <RowTypography>{row.datasetCreationDate || '--'}</RowTypography>;
export const RefreshTypeFormatter = ({ row }: { row: any }) => <RowTypography>{row.refreshType || '--'}</RowTypography>;
export const TableNameFormatter = ({ row }: { row: any }) => <RowTypography>{row.tableName || '--'}</RowTypography>;
export const MQueryFormatter = ({ row }: { row: any }) => <RowTypography>{row.mQuery || '--'}</RowTypography>;
export const UserFormatter = ({ row }: { row: any }) => <RowTypography>{row.user || '--'}</RowTypography>;
export const UserTypeFormatter = ({ row }: { row: any }) => <RowTypography>{row.userType || '--'}</RowTypography>;
export const LastLoginFormatter = ({ row }: { row: any }) => <RowTypography>{row.lastLogin || '--'}</RowTypography>;
export const MultiSectorAccessFormatter = ({ row }: { row: any }) => <RowTypography>{row.multiSectorAccess?.toString() || 'No'}</RowTypography>;
export const ActivityStatusFormatter = ({ row }: { row: any }) => <RowTypography>{row.activityStatus || '--'}</RowTypography>;

// Complex refresh schedule formatter with reduced complexity
export const RefreshScheduleFormatter = ({ row }: { row: any }) => {
  const schedule = row.refreshSchedule;
  
  if (!schedule) return <RowTypography>--</RowTypography>;
  

  if (typeof schedule === 'string') {
    return <RowTypography>{schedule}</RowTypography>;
  }
  
  if (typeof schedule === 'object') {
    return <RowTypography>{JSON.stringify(schedule)}</RowTypography>
  }
  
  return <RowTypography>--</RowTypography>;
};

// Extract the complex object handling logic
const RefreshScheduleObjectFormatter = ({ schedule }: { schedule: any }) => {
  try {
    const { days, times, enabled } = schedule;
    
    if (!enabled) {
      return <RowTypography>Disabled</RowTypography>;
    }
    
    if (!days || !times) {
      return <RowTypography>--</RowTypography>;
    }
    
    const dayCount = Array.isArray(days) ? days.length : 0;
    const timeCount = Array.isArray(times) ? times.length : 0;
    
    if (dayCount === 0 || timeCount === 0) {
      return <RowTypography>--</RowTypography>;
    }
    
    const firstTime = times[0];
    let displayText = `${dayCount} day(s) at ${firstTime}`;
    
    if (timeCount > 1) {
      displayText += ` +${timeCount - 1} more`;
    }
    
    return <RowTypography>{displayText}</RowTypography>;
  } catch {
  return <RowTypography>Invalid schedule</RowTypography>;
}
};